import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate.ts";
import { Activity, RotateCw } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styled from "styled-components";
import { GD } from "src/GD";
import { tooltipStyle } from "./constants";
import { getSessionsRateRequest, parseSessionsRate, type ISessionsRateData, type TSessionsRateTarget } from "./sessionsRateData";
import { StatCardHeader } from "./StatCardHeader";
import { ChartModeSelector } from "./ChartModeSelector";
import { useChartMode } from "./useChartMode";
import { statActionButtonStyles, statPanelSurface } from "./panelStyles";

const Panel = styled.section`
	${statPanelSurface}
	color: #344054;
	container-type: inline-size;
	.refresh { ${statActionButtonStyles} }
	.rate-actions { display: flex; align-items: center; gap: 8px; }
	.status { display: block; margin-top: 2px; font-size: 13px; line-height: 1.5; color: #667085; }
	.stat-card-header .toggle { min-height: 52px; padding: 10px 16px; }
	.stat-card-header .copy { display: flex; align-items: baseline; flex-wrap: wrap; gap: 2px 16px; }
	.stat-card-header .description { margin-top: 0; font-size: 12px; }
	.details:not([hidden]) { display: grid; grid-template-columns: minmax(400px, 30%) minmax(0, 1fr); align-items: stretch; gap: 16px; padding: 12px 16px; }
	dl { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-auto-rows: 1fr; gap: 8px; margin: 0; }
	dl > div { min-width: 0; padding: 8px 10px; border-radius: 8px; background: #f6f7fa; }
	dl > div.primary { background: #edf7fb; }
	dl > div.partial { background: #fff8e9; }
	dt { min-height: 18px; font-size: 12px; line-height: 18px; color: #667085; overflow-wrap: anywhere; }
	dd { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px; margin: 2px 0 0; font-size: 22px; line-height: 1.2; font-weight: 600; font-variant-numeric: tabular-nums; }
	dd > span { overflow-wrap: anywhere; }
	dd small { font-size: 12px; font-weight: 400; color: #667085; white-space: nowrap; }
	dl .caption { display: flex; flex-wrap: wrap; gap: 0 6px; font-size: 11px; line-height: 16px; color: #667085; }
	dl .caption:empty { display: none; }
	.trend { display: flex; flex-direction: column; min-width: 0; }
	.legend { display: flex; flex-wrap: wrap; gap: 4px 16px; margin: 0 0 4px; padding: 0; list-style: none; font-size: 12px; line-height: 18px; color: #667085; }
	.legend li { display: flex; align-items: center; gap: 6px; }
	.swatch { width: 16px; border-top: 3px solid; flex-shrink: 0; }
	.swatch.filled { width: 9px; height: 9px; border: 0; border-radius: 2px; }
	.chart { position: relative; flex: 1; width: 100%; min-height: 152px; min-width: 0; }
	/* Keep at least ~630px for the chart before stacking the two sections. */
	@container (max-width: 1099px) {
		.details:not([hidden]) { grid-template-columns: minmax(0, 1fr); gap: 10px; }
		dl { grid-template-columns: repeat(6, minmax(0, 1fr)); }
	}
	@container (max-width: 899px) { dl { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
	@container (max-width: 480px) {
		.stat-card-header .toggle { padding: 10px 12px; }
		.details:not([hidden]) { padding: 10px 12px 12px; }
		dl { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
	}
	@container (max-width: 230px) { dl { grid-template-columns: minmax(0, 1fr); } }
`;

const readCollapsed = (key: string) => {
	try { return localStorage.getItem(key) === "1"; } catch { return false; }
};

interface ISessionsRateCardProps {
	targetId: number;
	targetType: TSessionsRateTarget;
	projectId: number;
	userId: number;
}

export const SessionsRateCard = ({ targetId, targetType, projectId, userId }: ISessionsRateCardProps) => {
	useLocale();
	const { t, i18n } = useTranslation();
	const detailsId = useId();
	const preferenceKey = `sessions-rate-collapsed:${userId}`;
	const [preference, setPreference] = useState(() => ({ key: preferenceKey, collapsed: readCollapsed(preferenceKey) }));
	const collapsed = preference.key === preferenceKey ? preference.collapsed : readCollapsed(preferenceKey);
	const [attempt, setAttempt] = useState(0);
	const [chartMode, changeChartMode] = useChartMode(`sessions-rate-chart:${userId}`);
	const scope = `${userId}:${projectId}:${targetType}:${targetId}`;
	const [state, setState] = useState<{ scope: string; busy: boolean; data?: ISessionsRateData; error?: boolean }>({ scope, busy: true });
	const visibleState = state.scope === scope ? state : { busy: true, data: undefined, error: false };

	useEffect(() => {
		const sync = () => {
			setPreference({ key: preferenceKey, collapsed: readCollapsed(preferenceKey) });
		};
		sync();
		const onStorage = (event: StorageEvent) => { if (event.key === preferenceKey || event.key === null) sync(); };
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [preferenceKey]);
	const toggleCollapsed = () => {
		const next = !collapsed;
		setPreference({ key: preferenceKey, collapsed: next });
		try { localStorage.setItem(preferenceKey, next ? "1" : "0"); } catch { return; }
	};

	useEffect(() => {
		const controller = new AbortController();
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { controller.abort(); setState({ scope, busy: false }); });
		setState(previous => ({ scope, busy: true, data: previous.scope === scope ? previous.data : undefined }));
		const load = async () => {
			let contextChanged = false;
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (controller.signal.aborted || profile?.id !== userId || project?.id !== projectId) {
					contextChanged = true;
					throw new Error("Context changed");
				}
			};
			try {
				await checkContext();
				const response = await GD.REQ_API.request({ ...getSessionsRateRequest(projectId, targetId, targetType), signal: controller.signal });
				await checkContext();
				if (!response || response.error || response.data === undefined) throw new Error(msg("sessionRateRequestFailed"));
				const data = parseSessionsRate(response.data, targetId, targetType);
				if (!data) throw new Error(msg("unexpectedSessionRateResponse"));
				if (!controller.signal.aborted) setState({ scope, busy: false, data });
			} catch {
				if (!controller.signal.aborted) setState(previous => ({ scope, busy: false, error: true, data: !contextChanged && previous.scope === scope ? previous.data : undefined }));
			}
		};
		void load();
		return () => {
			controller.abort();
			GD.S_AUTH_LOG_OUT.unsubscribe(logout);
		};
	}, [targetId, targetType, projectId, userId, attempt, scope]);

	const data = visibleState.data;
	const format = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
	const paymentsLabel = t("dashboard.sessionRate.payments");
	const time = (value: string | null | undefined) => value ? <time dateTime={value.replace(" ", "T")} title={value}>{value.slice(11)}</time> : null;
	const metrics = data ? [
		{ key: "last", label: t("dashboard.sessionRate.lastMinute"), value: data.lastMinute?.payments ?? null, caption: time(data.lastMinute?.minute), tone: "primary", rate: true },
		{ key: "current", label: t("dashboard.sessionRate.currentMinute"), value: data.currentMinute?.payments ?? null, caption: <>{time(data.currentMinute?.minute)}{data.currentMinute?.partial && <span>{t("dashboard.sessionRate.partial", { seconds: data.currentMinute.secondsElapsed ?? "—" })}</span>}</>, tone: "partial", rate: false },
		{ key: "peak", label: t("dashboard.sessionRate.peak"), value: data.peak?.payments ?? null, caption: data.peak ? time(data.peak.minute) : null, tone: "peak", rate: data.peak !== null },
		{ key: "avg5", label: t("dashboard.sessionRate.average", { minutes: 5 }), value: data.avg5m?.payments ?? null, caption: null, rate: true },
		{ key: "avg15", label: t("dashboard.sessionRate.average", { minutes: 15 }), value: data.avg15m?.payments ?? null, caption: null, rate: true },
		{ key: "avgWindow", label: t("dashboard.sessionRate.average", { minutes: data.windowMinutes ?? "—" }), value: data.avgWindow?.payments ?? null, caption: null, rate: true },
	] : [];
	const chartSeries = [
		{ key: "payments", label: paymentsLabel, color: "#167ca6" },
		{ key: "completed", label: t("dashboard.sessionRate.completed"), color: "#21875b" },
		{ key: "failed", label: t("dashboard.sessionRate.failed"), color: "#c43e51" },
	];
	const plottedSeries = chartMode === "area" ? [...chartSeries].reverse() : chartSeries;
	const description = visibleState.error ? t("dashboard.sessionRate.error") : data
		? `${t("dashboard.sessionRate.window", { minutes: data.windowMinutes ?? "—" })} · ${t("dashboard.sessionRate.asOf")} ${data.asOf ?? "—"}`
		: t(visibleState.busy ? "common.loading" : "dashboard.sessionRate.empty");
	const refreshLabel = t(visibleState.busy ? "common.refreshing" : "common.refresh");

	return <Panel aria-label={t("dashboard.sessionRate.title")} aria-busy={visibleState.busy}>
		<StatCardHeader title={t("dashboard.sessionRate.title")} label={t(collapsed ? "dashboard.sessionRate.expand" : "dashboard.sessionRate.collapse")}
			icon={<Activity size={20} color="#167ca6" aria-hidden="true" />} description={description} descriptionTitle={description} descriptionColor={visibleState.error ? "#b42332" : undefined}
			expanded={!collapsed} controls={detailsId} onToggle={toggleCollapsed}
			actionWidth={146} action={<div className="rate-actions">
				<ChartModeSelector value={chartMode} onChange={changeChartMode} />
				<button className="refresh" type="button" disabled={visibleState.busy} aria-busy={visibleState.busy} title={refreshLabel} aria-label={refreshLabel} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></button>
			</div>}
			preview={data && data.series.length > 0 ? <AreaChart width={280} height={36} data={data.series} accessibilityLayer={false} margin={{ top: 3, right: 2, bottom: 3, left: 2 }}>
				<Area dataKey="payments" type="linear" stroke="#167ca6" strokeWidth={2} fill="#edf7fb" fillOpacity={1} dot={data.series.length === 1} activeDot={false} isAnimationActive={false} />
			</AreaChart> : undefined} />
		<div id={detailsId} className="details" hidden={collapsed || !data}>
		{data && !collapsed && <>
			<dl aria-label={paymentsLabel}>{metrics.map(item => <div key={item.key} className={item.tone}>
				<dt>{item.label}</dt><dd><span>{item.value === null ? "—" : format(item.value)}</span>{item.rate && item.value !== null && <small>{t("dashboard.sessionRate.perMinute")}</small>}</dd><span className="caption">{item.caption}</span>
			</div>)}</dl>
			<div className="trend">
			<ul className="legend">{chartSeries.map(series => {
				const filled = chartMode === "bar" || (chartMode === "area" && series.key !== "payments");
				return <li key={series.key}><span className={`swatch${filled ? " filled" : ""}`} aria-hidden="true" style={{ backgroundColor: filled ? series.color : undefined, borderColor: series.color, borderTopStyle: chartMode === "line" && series.key === "failed" ? "dashed" : "solid" }} />{series.label}</li>;
			})}</ul>
			{data.series.length > 0 ? <div className="chart" role="group" aria-label={t("dashboard.sessionRate.chart")}>
				<ResponsiveContainer width="100%" height="100%" minWidth={0} style={{ position: "absolute", inset: 0 }} initialDimension={{ width: 320, height: 152 }}>
					<ComposedChart data={data.series} accessibilityLayer margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barGap={0} barCategoryGap="18%">
						<CartesianGrid stroke="#edf0f4" strokeDasharray="3 3" vertical={false} />
						<XAxis dataKey="minute" tickFormatter={value => String(value).slice(11)} tick={{ fontSize: 11, fill: "#848799" }} height={20} tickSize={0} tickMargin={6} axisLine={false} tickLine={false} minTickGap={36} />
						<YAxis allowDecimals={false} domain={[0, "auto"]} width={32} tick={{ fontSize: 11, fill: "#848799" }} axisLine={false} tickLine={false} />
						<Tooltip contentStyle={{ ...tooltipStyle, maxWidth: 240 }} formatter={value => format(Number(value))} />
						{plottedSeries.map(series => chartMode === "bar"
							? <Bar key={series.key} dataKey={series.key} name={series.label} fill={series.color} radius={[2, 2, 0, 0]} maxBarSize={12} isAnimationActive={false} />
							: chartMode === "area" && series.key !== "payments"
								? <Area key={series.key} dataKey={series.key} name={series.label} stackId="outcomes" type="linear" stroke="none" fill={series.color} fillOpacity={0.3} dot={data.series.length === 1} activeDot={{ r: 4 }} isAnimationActive={false} />
							: <Line key={series.key} dataKey={series.key} name={series.label} type="linear" stroke={series.color} strokeWidth={chartMode === "area" ? 1 : series.key === "payments" ? 2.5 : 1.5} strokeDasharray={series.key === "failed" ? "4 3" : undefined} dot={data.series.length === 1} activeDot={{ r: 4 }} isAnimationActive={false} />)}
					</ComposedChart>
				</ResponsiveContainer>
			</div> : <div className="status">{t("dashboard.sessionRate.empty")}</div>}
			</div>
		</>}
		</div>
	</Panel>;
};
